const normalizeForMatch = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[\s]+/g, " ")
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201c\u201d]/g, '"')
    .replace(/[.,!?;:]+/g, "")
    .trim();

const tail = (value: string, maxLength: number): string =>
  value.length <= maxLength ? value : value.slice(value.length - maxLength);

const computeOverlap = (left: string, right: string, minLength = 4): number => {
  const maxLength = Math.min(left.length, right.length);

  for (let overlap = maxLength; overlap >= minLength; overlap -= 1) {
    if (left.slice(left.length - overlap) === right.slice(0, overlap)) {
      return overlap;
    }
  }

  return 0;
};

export interface TranscriptMergeResult {
  recognizingText?: string;
  recognizedText?: string;
  dedupedChars: number;
}

export class TranscriptMerger {
  private finalizedTranscript = "";
  private currentPartial = "";
  private dedupedChars = 0;

  ingestFinal(text: string): TranscriptMergeResult {
    if (!text || !text.trim()) {
      return { dedupedChars: 0 };
    }

    const finalTail = tail(this.finalizedTranscript, 400);
    const normalizedTail = normalizeForMatch(finalTail);
    const normalizedIncoming = normalizeForMatch(text);

    let overlapLength = 0;
    if (normalizedTail && normalizedIncoming) {
      overlapLength = computeOverlap(normalizedTail, normalizedIncoming, 4);
    }

    let recognizedText = text;
    if (overlapLength > 0) {
      const safeCut = Math.min(overlapLength, recognizedText.length);
      recognizedText = recognizedText.slice(safeCut).trimStart();
      this.dedupedChars += safeCut;
    }

    if (!recognizedText) {
      return {
        dedupedChars: overlapLength,
      };
    }

    if (this.finalizedTranscript.length > 0) {
      this.finalizedTranscript = `${this.finalizedTranscript} ${recognizedText}`;
    } else {
      this.finalizedTranscript = recognizedText;
    }

    this.currentPartial = "";

    return {
      recognizedText,
      dedupedChars: overlapLength,
    };
  }

  ingestPartial(text: string): TranscriptMergeResult {
    this.currentPartial = text;
    return {
      recognizingText: this.currentPartial,
      dedupedChars: 0,
    };
  }

  getFinalizedTranscript(): string {
    return this.finalizedTranscript;
  }

  getCurrentPartial(): string {
    return this.currentPartial;
  }

  getDedupedChars(): number {
    return this.dedupedChars;
  }

  reset(): void {
    this.finalizedTranscript = "";
    this.currentPartial = "";
    this.dedupedChars = 0;
  }
}
